/**
 * DatePickerComponent - Reusable Date Picker Component
 * 
 * A flexible and reusable date picker component that integrates with Angular forms
 * using ControlValueAccessor interface. Supports both template-driven and reactive forms.
 * 
 * Function Index:
 * 1. constructor - Initializes the component with required services
 * 2. ngOnInit - Angular lifecycle hook that initializes the component
 * 3. ngOnChanges - Angular lifecycle hook that responds to input property changes
 * 4. ngAfterViewInit - Angular lifecycle hook executed after view initialization
 * 5. writeValue - Writes a new value to the date picker (ControlValueAccessor)
 * 6. registerOnChange - Registers change callback function (ControlValueAccessor)
 * 7. registerOnTouched - Registers touched callback function (ControlValueAccessor)
 * 8. setDisabledState - Sets the disabled state of the component (ControlValueAccessor)
 * 9. handleDateChange - Handles date change events and emits the formatted date
 * 10. onCalendarBlur - Handles blur event for the calendar
 * 11. clearDate - Clears the selected date
 * 12. formatDateValue - Formats date value to the specified format (private)
 * 13. hasError - Getter that checks if the component has validation errors
 * 14. errors - Getter that returns validation errors from the form control
 * 15. calendarMaxDate - Getter that returns maxDate for calendar binding
 * 16. calendarMinDate - Getter that returns minDate for calendar binding
 */

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule } from '@angular/forms';
import * as moment from 'moment';
import { CalendarModule } from 'primeng/calendar';

@Component({
  standalone: true,
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  imports: [CommonModule, FormsModule, CalendarModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
  ],
})
export class DatePickerComponent implements OnInit, OnChanges, AfterViewInit, ControlValueAccessor {
  ngControl: NgControl | null = null;
  selectedDate: Date | null = null;
  isDisabled = false;

  // Callback functions for ControlValueAccessor
  private onChange: (value: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  /**
   * Input: Default/initial date value
   */
  @Input() defaultDate: Date | null = null;

  /**
   * Input: Array of disabled day numbers (0 = Sunday, 1 = Monday, etc.)
   */
  @Input() daysDisabled: number[] = [];

  /**
   * Input: Maximum selectable date
   */
  @Input() 
  set maxDate(value: Date | undefined) {
    this._maxDate = value;
  }
  get maxDate(): Date | undefined {
    return this._maxDate;
  }
  private _maxDate?: Date;

  /**
   * Input: Minimum selectable date
   */
  @Input()
  set minDate(value: Date | undefined) {
    this._minDate = value;
  }
  get minDate(): Date | undefined {
    return this._minDate;
  }
  private _minDate?: Date;

  /**
   * Input: Whether the input field is readonly
   */
  @Input() readonlyInput = false;

  /**
   * Input: Placeholder text for the input field
   */
  @Input() placeholder = 'YYYY-MM-DD';

  /**
   * Input: Date format for display (PrimeNG format)
   */
  @Input() dateFormat = 'yy-mm-dd';

  /**
   * Input: Output date format for emitted values (moment.js format)
   */
  @Input() outputFormat = 'YYYY-MM-DD';

  /**
   * Input: First day of the week (0 = Sunday, 1 = Monday, etc.)
   */
  @Input() firstDayOfWeek = 1;

  /**
   * Input: Show icon/button to open calendar
   */
  @Input() showIcon = false;

  /**
   * Input: Icon to display
   */
  @Input() icon = 'pi pi-calendar';

  /**
   * Input: Show button bar (Today/Clear buttons)
   */
  @Input() showButtonBar = false;

  /**
   * Input: Enable manual input
   */
  @Input() manualInput = true;

  /**
   * Input: Show calendar icon
   */
  @Input() showClear = false;

  /**
   * Input: Keep calendar open after date selection
   */
  @Input() keepOpen = false;

  /**
   * Input: CSS class for input styling
   */
  @Input() inputStyleClass = 'form-control custom-placeholder';

  /**
   * Input: Append calendar to body or element
   */
  @Input() appendTo: 'body' | null = 'body';

  /**
   * Input: Enable inline calendar mode
   */
  @Input() inline = false;

  /**
   * Input: View mode (date, month, year)
   */
  @Input() view: 'date' | 'month' | 'year' = 'date';

  /**
   * Input: Selection mode (single, multiple, range)
   */
  @Input() selectionMode: 'single' | 'multiple' | 'range' = 'single';

  /**
   * Input: Number of months to display
   */
  @Input() numberOfMonths = 1;

  /**
   * Input: Show week numbers
   */
  @Input() showWeek = false;

  /**
   * Output: Emits the selected date as formatted string
   */
  @Output() selectedDateOutput = new EventEmitter<string | null>();

  /**
   * Output: Emits when date is cleared
   */
  @Output() dateCleared = new EventEmitter<void>();

  /**
   * Output: Emits the selected Date object
   */
  @Output() dateObjectOutput = new EventEmitter<Date | null>();

  /**
   * 1. constructor - Initializes the component with required services
   * @param injector - Angular injector for dependency injection
   * @param changeDetector - Change detector reference for manual change detection
   */
  constructor(
    private injector: Injector,
    private changeDetector: ChangeDetectorRef,
  ) {}

  /**
   * 2. ngOnInit - Angular lifecycle hook that initializes the component
   * Sets the initial date from defaultDate input
   */
  ngOnInit(): void {
    if (this.defaultDate) {
      this.selectedDate = new Date(this.defaultDate);
    }
  }

  /**
   * 3. ngOnChanges - Angular lifecycle hook that responds to input property changes
   * Updates date and configuration when input properties change
   * @param changes - Object containing the changed properties
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaultDate']?.currentValue) {
      this.selectedDate = new Date(changes['defaultDate'].currentValue);
    }
  }

  /**
   * 4. ngAfterViewInit - Angular lifecycle hook executed after view initialization
   * Retrieves NgControl from injector and triggers change detection
   */
  ngAfterViewInit(): void {
    const ngControl = this.injector.get(NgControl, null);
    if (ngControl != null) {
      this.ngControl = ngControl;
    }
    this.changeDetector.detectChanges();
  }

  /**
   * 5. writeValue - Writes a new value to the date picker
   * Part of ControlValueAccessor implementation for Angular forms integration
   * @param value - The date value to write to the picker (can be Date, string, or null)
   */
  writeValue(value: Date | string | null): void {
    if (value) {
      // Handle both Date objects and string values
      this.selectedDate = value instanceof Date ? value : new Date(value);
    } else {
      this.selectedDate = null;
    }
    this.changeDetector.markForCheck();
  }

  /**
   * 6. registerOnChange - Registers change callback function
   * Part of ControlValueAccessor implementation for Angular forms integration
   * @param fn - Callback function to be invoked when the value changes
   */
  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  /**
   * 7. registerOnTouched - Registers touched callback function
   * Part of ControlValueAccessor implementation for Angular forms integration
   * @param fn - Callback function to be invoked when the component is touched
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * 8. setDisabledState - Sets the disabled state of the component
   * Part of ControlValueAccessor implementation for Angular forms integration
   * @param isDisabled - Whether the component should be disabled
   */
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.changeDetector.markForCheck();
  }

  /**
   * 9. handleDateChange - Handles date change events and emits the formatted date
   * Formats the selected date and notifies form control and parent components
   * @param event - The selected date from the datepicker
   */
  handleDateChange(date: Date | string | null): void {
    const formattedDate = this.formatDateValue(date);

    // Notify Angular forms
    this.onChange(formattedDate);
    this.onTouched();

    // Emit to parent components
    this.selectedDateOutput.emit(formattedDate);
    this.dateObjectOutput.emit(date instanceof Date ? date : null);
  }

  /**
   * 10. onCalendarBlur - Handles blur event for the calendar
   * Marks the component as touched when user leaves the field
   */
  onCalendarBlur(): void {
    this.onTouched();
  }

  /**
   * 11. clearDate - Clears the selected date
   * Resets the date value and notifies the form control
   */
  clearDate(): void {
    this.selectedDate = null;
    this.onChange(null);
    this.onTouched();
    this.selectedDateOutput.emit(null);
    this.dateObjectOutput.emit(null);
    this.dateCleared.emit();
  }

  /**
   * 12. formatDateValue - Formats date value to the specified format
   * @param date - The date to format
   * @returns Formatted date string or null
   */
  private formatDateValue(date: Date | string | null): string | null {
    if (!date || !(date instanceof Date)) {
      return null;
    }

    try {
      const m = moment(date);
      if (!m.isValid()) {
        return null;
      }
      return m.format(this.outputFormat || 'YYYY-MM-DD');
    } catch (error) {
      console.error('Error formatting date:', error);
      return null;
    }
  }

  /**
   * 13. hasError - Getter that checks if the component has validation errors
   * Checks if the form control has validation errors and has been touched
   * @returns True if the component has errors and has been touched, false otherwise
   */
  get hasError(): boolean {
    return !!(this.ngControl?.invalid && this.ngControl?.touched);
  }

  /**
   * 14. errors - Getter that returns validation errors from the form control
   * Retrieves the current validation errors object from the associated form control
   * @returns Validation errors object or null if no errors
   */
  get errors(): any {
    return this.ngControl?.errors;
  }

  /**
   * 15. calendarMaxDate - Getter that returns maxDate for calendar binding
   * Converts optional Date to any type for PrimeNG calendar compatibility
   * @returns Maximum date value or undefined
   */
  get calendarMaxDate(): any {
    return this.maxDate;
  }

  /**
   * 16. calendarMinDate - Getter that returns minDate for calendar binding
   * Converts optional Date to any type for PrimeNG calendar compatibility
   * @returns Minimum date value or undefined
   */
  get calendarMinDate(): any {
    return this.minDate;
  }
}
